import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import toast from 'react-hot-toast'

export default function LoginPage() {
  const [email, setEmail]           = useState('')
  const [password, setPassword]     = useState('')
  const [showPassword, setShowPw]   = useState(false)
  const [isLoading, setIsLoading]   = useState(false)
  const [errors, setErrors]         = useState({})
  const navigate  = useNavigate()
  const location  = useLocation()
  const { login } = useAuthStore()

  const returnUrl = location.state?.returnUrl || '/app/dashboard'

  const validate = () => {
    const errs = {}
    if (!email) errs.email = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = 'Enter a valid email address'
    if (!password) errs.password = 'Password is required'
    return errs
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setErrors({})
    setIsLoading(true)
    try {
      const data = await login(email, password)
      toast.success('Welcome back!')
      navigate(data?.onboarding_complete === false ? '/onboarding' : returnUrl, { replace: true })
    } catch (error) {
      toast.error(error.message || 'Sign in failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const inputCls = (hasError) => [
    'w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-all duration-200',
    'bg-white border text-slate-900 placeholder:text-slate-400',
    hasError ? 'border-red-400 focus:border-red-400' : 'border-slate-200 focus:border-blue-400',
    'focus:ring-2 focus:ring-blue-500/20',
  ].join(' ')

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* ── Left panel (dark) 42% ── */}
      <div
        className="hidden lg:flex flex-col w-[42%] relative overflow-hidden"
        style={{ background: '#0f172a' }}
      >
        {/* Grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
        {/* Blue glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '-80px', right: '-80px',
            width: '360px', height: '360px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(37,99,235,0.25) 0%, transparent 70%)',
          }}
        />

        {/* Logo */}
        <div className="relative z-10 p-8">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
              style={{ background: '#2563eb', color: '#fff' }}>A</div>
            <div className="flex items-baseline gap-1">
              <span className="font-semibold text-sm tracking-wide" style={{ color: '#f1f5f9' }}>AcdyON</span>
              <span className="italic text-sm" style={{ color: '#60a5fa', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Scholar</span>
            </div>
          </div>
        </div>

        {/* Quote */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-12 pb-16">
          <blockquote className="text-center max-w-xs">
            <p style={{
              fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
              fontSize: '1.5rem',
              lineHeight: '1.45',
              color: '#e2e8f0',
              fontStyle: 'italic',
            }}>
              "Every great thesis begins with a single, well-placed idea."
            </p>
            <div className="mx-auto mt-7 mb-5" style={{ width: 40, height: 1, background: '#2563eb' }} />
            <cite className="not-italic text-sm" style={{ color: '#64748b' }}>
              — AcdyON Scholar Platform
            </cite>
          </blockquote>

          {/* Feature pills */}
          <div className="mt-12 flex flex-wrap gap-2 justify-center">
            {['SOP Generator', 'Thesis Writer', 'Research Assistant', 'References'].map(f => (
              <span key={f} className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: 'rgba(37,99,235,0.15)', color: '#93c5fd', border: '1px solid rgba(37,99,235,0.25)' }}>
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel (white) 58% ── */}
      <div className="flex-1 flex items-center justify-center px-8 py-12" style={{ background: '#ffffff' }}>
        <div className="w-full max-w-[420px]">

          {/* Heading */}
          <div className="mb-8">
            <h1 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '2rem', color: '#0f172a', fontWeight: 700, lineHeight: 1.2 }}>
              Welcome back
            </h1>
            <p className="mt-2 text-sm" style={{ color: '#64748b' }}>
              Sign in to continue your research journey
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#475569' }}>
                Email
              </label>
              <input
                type="email"
                placeholder="you@university.edu"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                className={inputCls(!!errors.email)}
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#475569' }}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className={inputCls(!!errors.password) + ' pr-12'}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: '#94a3b8' }}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
            </div>

            {/* Forgot */}
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs transition-colors" style={{ color: '#2563eb' }}>
                Forgot password?
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
              style={{ background: '#2563eb', color: '#ffffff' }}
              onMouseEnter={e => { if (!isLoading) e.currentTarget.style.background = '#1d4ed8' }}
              onMouseLeave={e => { if (!isLoading) e.currentTarget.style.background = '#2563eb' }}
            >
              {isLoading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: '#e2e8f0' }} />
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 text-xs" style={{ background: '#ffffff', color: '#94a3b8' }}>or continue with</span>
            </div>
          </div>

          {/* Google OAuth */}
          <button
            type="button"
            onClick={() => toast('Google OAuth coming soon')}
            className="w-full flex items-center justify-center gap-3 py-2.5 rounded-lg text-sm font-medium transition-all"
            style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Register link */}
          <p className="mt-6 text-center text-sm" style={{ color: '#64748b' }}>
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold transition-colors" style={{ color: '#2563eb' }}>
              Create one →
            </Link>
          </p>

        </div>
      </div>
    </div>
  )
}
